import type { ElementSettingsFormField, ElementSettingsFormSchema } from '../elements/types';
import { isRecord } from '../../../../utils/typeGuards';
import { getRowsFieldItemFields } from './settingsFormSchemaRuntime/shared';
import { getSwarmTypeOptionsOrThrow } from './swarmTypesFieldContract';

function isBaseFieldDefinition(value: unknown): value is ElementSettingsFormField {
    return (
        isRecord(value) &&
        typeof value.name === 'string' &&
        typeof value.label === 'string' &&
        typeof value.type === 'string' &&
        Object.prototype.hasOwnProperty.call(value, 'default')
    );
}

function assertNestedFields(
    field: ElementSettingsFormField,
    fieldPath: string
): readonly ElementSettingsFormField[] {
    const nestedFields = field.fields;
    if (!Array.isArray(nestedFields) || nestedFields.length === 0) {
        throw new Error(`Expected "${fieldPath}" ${field.type} field to define nested fields.`);
    }

    const validatedFields = nestedFields.filter(isBaseFieldDefinition);
    if (validatedFields.length !== nestedFields.length) {
        throw new Error(`Expected "${fieldPath}" nested field definitions to be valid fields.`);
    }

    return validatedFields;
}

function assertStringListFieldMetadata(field: ElementSettingsFormField, fieldPath: string): void {
    if (typeof field.itemLabel !== 'string' || field.itemLabel.trim().length === 0) {
        throw new Error(`Expected "${fieldPath}.itemLabel" to be a non-empty string.`);
    }
}

function assertSwarmTypesFieldMetadata(field: ElementSettingsFormField, fieldPath: string): void {
    getSwarmTypeOptionsOrThrow(field.options, `${fieldPath}.options`);
}

function assertRowsFieldMetadata(field: ElementSettingsFormField, fieldPath: string): void {
    if (!Number.isInteger(field.maxRows) || (field.maxRows ?? 0) <= 0) {
        throw new Error(`Expected "${fieldPath}.maxRows" to be a positive integer.`);
    }
}

function assertSettingsFormFieldMetadata(field: ElementSettingsFormField, fieldPath: string): void {
    if (field.type === 'stringList') {
        assertStringListFieldMetadata(field, fieldPath);
        return;
    }

    if (field.type === 'swarmTypes') {
        assertSwarmTypesFieldMetadata(field, fieldPath);
        return;
    }

    if (field.type !== 'rows' && field.type !== 'group') {
        return;
    }

    const nestedFields = assertNestedFields(field, fieldPath);
    if (field.type === 'rows') {
        assertRowsFieldMetadata(field, fieldPath);
        getRowsFieldItemFields(field);
    }

    nestedFields.forEach((nestedField) => {
        assertSettingsFormFieldMetadata(nestedField, `${fieldPath}.${nestedField.name}`);
    });
}

// Validates developer-authored form-field metadata (`settingsFormSchema`), not
// element/template settings payload values.
export function assertSettingsFormSchemaMetadata(
    settingsFormSchema: ElementSettingsFormSchema
): void {
    settingsFormSchema.fields.forEach((field) => {
        assertSettingsFormFieldMetadata(field, field.name);
    });
}
